import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid
} from 'recharts';
import { AlertTriangle, BookOpen, Clock } from 'lucide-react';
import {
  resolveRubricDimensions,
  getDimensionRatings,
  ratingToScore,
  QUALITY_SELECT_COLUMNS,
} from '../lib/qualityDimensions';
import { defaultPolicy } from '../lib/issuePolicy';
import { ISSUE_SELECT_COLUMNS, LATENESS_CATEGORY, countOpenIssues } from '../lib/issueCounting';

// PostgREST caps a response at 1000 rows, so page through the org's risks
// rather than trusting a single unbounded read.
const RISK_PAGE_SIZE = 1000;
const RISK_PAGE_LIMIT = 50; // hard stop; 50k risks is far beyond any real org

// Everything that could be an issue for this org, for lib/issueCounting.js to
// judge. `category` is NOT NULL in risks_v2, so dropping tutor_lateness in SQL
// is exactly the helper's lateness rule — it just moves less data.
const fetchOrgIssueRows = async (orgId) => {
  const rows = [];
  for (let p = 0; p < RISK_PAGE_LIMIT; p++) {
    const from = p * RISK_PAGE_SIZE;
    const { data, error } = await supabase
      .from('risks_v2')
      .select(ISSUE_SELECT_COLUMNS)
      .eq('organization_id', orgId)
      .neq('category', LATENESS_CATEGORY)
      .order('id')
      .range(from, from + RISK_PAGE_SIZE - 1);
    if (error || !data) break;
    rows.push(...data);
    if (data.length < RISK_PAGE_SIZE) break;
  }
  return rows;
};

const timeRangeOptions = [
  { label: '7d', value: 7 },
  { label: '28d', value: 28 },
  { label: '90d', value: 90 },
  { label: '1y', value: 365 },
  { label: 'All', value: null },
];

// PostgREST caps responses at 1000 rows; on 1y/All the org has more sessions
// than that, so unpaged fetches silently truncate charts and totals.
const fetchAllPages = async (buildPageQuery, pageSize = 1000) => {
  const rows = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await buildPageQuery().range(from, from + pageSize - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < pageSize) break;
  }
  return rows;
};

const Dashboard = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [initialLoad, setInitialLoad] = useState(true);
  // True during every reload (including range switches), so the skeleton
  // shows instead of stale numbers from the previous range.
  const [rangeLoading, setRangeLoading] = useState(false);
  const [timeRange, setTimeRange] = useState(28);
  const loadingRef = useRef(false);

  const [stats, setStats] = useState({ totalSessions: 0, processedSessions: 0, totalMinutes: 0, openIssues: 0 });
  const [prevStats, setPrevStats] = useState(null);
  const [quality, setQuality] = useState({ strong: 0, adequate: 0, needsDev: 0 });
  const [sessionsPerDay, setSessionsPerDay] = useState([]);
  const [minutesPerDay, setMinutesPerDay] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);

  useEffect(() => {
    if (!organizationId) return;
    loadDashboard();
  }, [organizationId, timeRange]);

  const loadDashboard = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setRangeLoading(true);
    try {
      const orgId = organizationId;
      let dateFilter = null;
      if (timeRange) {
        const d = new Date();
        d.setDate(d.getDate() - timeRange);
        dateFilter = d.toISOString();
      }

      const buildQuery = (query) => dateFilter ? query.gte('lesson_timestamp', dateFilter) : query;

      // Parallel queries — optimized: minimal columns, no heavy joins
      const [
        { count: totalSessions },
        { count: processedSessions },
        { data: sessionsData },
        issueRows,
        { data: recentData },
        { data: qaData },
        { data: orgRow },
      ] = await Promise.all([
        buildQuery(supabase.from('sessions').select('id', { count: 'exact', head: true }).eq('organization_id', orgId)),
        // Reviewed = rows in quality_assessments scoped to org + session window via inner join
        (() => {
          let q = supabase.from('quality_assessments')
            .select('session_id, sessions!inner(organization_id, lesson_timestamp)', { count: 'exact', head: true })
            .eq('sessions.organization_id', orgId);
          if (dateFilter) q = q.gte('sessions.lesson_timestamp', dateFilter);
          return q;
        })(),
        // Only fetch timestamp + duration for charts (no joins), paged past the 1000-row cap
        (async () => ({
          data: await fetchAllPages(() => buildQuery(
            supabase.from('sessions').select('lesson_timestamp, duration_minutes')
              .eq('organization_id', orgId).eq('is_processed_v2', true).order('lesson_timestamp')
          )),
        }))(),
        // Open-issue rows; the count itself is taken by the shared helper below
        // so this tile agrees with the Issues page and the tutor pages.
        fetchOrgIssueRows(orgId),
        // Recent sessions with QA for the list
        buildQuery(supabase.from('sessions').select(`id, tutor_name, student_name, lesson_timestamp, subject_covered, duration_minutes, quality_assessments(${QUALITY_SELECT_COLUMNS}, insufficient_evidence)`)
          .eq('organization_id', orgId).eq('is_processed_v2', true).order('lesson_timestamp', { ascending: false }).limit(8)),
        // Quality assessments separately — paged past the 1000-row cap
        (async () => ({
          data: await fetchAllPages(() => {
            let q = supabase.from('quality_assessments').select(`${QUALITY_SELECT_COLUMNS}, insufficient_evidence, sessions!inner(organization_id, lesson_timestamp)`)
              .eq('sessions.organization_id', orgId).order('id');
            if (dateFilter) q = q.gte('sessions.lesson_timestamp', dateFilter);
            return q;
          }),
        }))(),
        // Org rubric — drives which dimensions the quality averages read.
        // issue_filter_policy feeds the shared issue-counting rules.
        supabase.from('organizations').select('quality_rubric, issue_filter_policy').eq('id', orgId).maybeSingle(),
      ]);

      // The org's dimension set (falls back to the five built-ins).
      const rubricDims = resolveRubricDimensions(orgRow?.quality_rubric);

      // Single source of truth for "flagged issue": lib/issueCounting.js.
      // Excludes tutor_lateness and guardrail-suppressed risks (the latter read
      // off the LLM-stamped filter_status), exactly as the Issues page does.
      const issuePolicy = orgRow?.issue_filter_policy || defaultPolicy();
      const openIssues = countOpenIssues(issueRows, issuePolicy);

      // Average the rubric's dimensions for one assessment row.
      // Unrated / unknown dimensions score 0 and are excluded, exactly as before.
      const avgRating = (qa) => {
        if (!qa || qa.insufficient_evidence) return null;
        const ratings = getDimensionRatings(qa);
        const scores = rubricDims
          .map(d => ratingToScore(ratings[d.key]))
          .filter(x => x > 0);
        if (scores.length === 0) return null;
        return scores.reduce((a, b) => a + b, 0) / scores.length;
      };

      const totalMinutes = (sessionsData || []).reduce((sum, s) => sum + (s.duration_minutes || 0), 0);

      setStats({ totalSessions: totalSessions || 0, processedSessions: processedSessions || 0, totalMinutes, openIssues });

      // Load previous period stats for trend indicators
      if (timeRange) {
        try {
          const prevEnd = new Date();
          prevEnd.setDate(prevEnd.getDate() - timeRange);
          const prevStart = new Date(prevEnd);
          prevStart.setDate(prevStart.getDate() - timeRange);
          const [
            { count: prevProcessed },
            { data: prevSessData },
          ] = await Promise.all([
            supabase.from('quality_assessments')
              .select('session_id, sessions!inner(organization_id, lesson_timestamp)', { count: 'exact', head: true })
              .eq('sessions.organization_id', orgId)
              .gte('sessions.lesson_timestamp', prevStart.toISOString())
              .lt('sessions.lesson_timestamp', prevEnd.toISOString()),
            supabase.from('sessions').select('duration_minutes')
              .eq('organization_id', orgId).eq('is_processed_v2', true)
              .gte('lesson_timestamp', prevStart.toISOString()).lt('lesson_timestamp', prevEnd.toISOString()),
          ]);
          const prevMinutes = (prevSessData || []).reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
          setPrevStats({ processedSessions: prevProcessed || 0, totalMinutes: prevMinutes });
        } catch { setPrevStats(null); }
      } else {
        setPrevStats(null);
      }

      // Quality distribution from qaData
      let strong = 0, adequate = 0, needsDev = 0;
      (qaData || []).forEach(qa => {
        // Buckets by rating band, not by dimension — abstained rows are skipped.
        const avg = avgRating(qa);
        if (avg === null) return;
        if (avg >= 2.5) strong++;
        else if (avg >= 1.5) adequate++;
        else needsDev++;
      });
      setQuality({ strong, adequate, needsDev });

      // Chart data — span the whole selected window with zero-filled days, so
      // a 1y view shows the quiet period before lessons started instead of
      // starting the axis at the first lesson. "All" still starts at the
      // first lesson (no meaningful window start exists).
      const dayMap = {};
      const minMap = {};
      (sessionsData || []).forEach(s => {
        const day = s.lesson_timestamp?.split('T')[0];
        if (!day) return;
        dayMap[day] = (dayMap[day] || 0) + 1;
        minMap[day] = (minMap[day] || 0) + (s.duration_minutes || 0);
      });
      const dataDays = Object.keys(dayMap).sort();
      const startDay = dateFilter ? dateFilter.split('T')[0] : dataDays[0];
      const series = [];
      if (startDay) {
        const cursor = new Date(`${startDay}T00:00:00Z`);
        const endDate = new Date(`${new Date().toISOString().split('T')[0]}T00:00:00Z`);
        while (cursor <= endDate) {
          series.push(cursor.toISOString().split('T')[0]);
          cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
      }
      setSessionsPerDay(series.map(d => ({ date: d, count: dayMap[d] || 0 })));
      setMinutesPerDay(series.map(d => ({ date: d, minutes: Math.round(minMap[d] || 0) })));

      // Recent sessions
      const getQuality = (qa) => {
        const avg = avgRating(qa);
        if (avg === null) return 'N/A';
        return avg >= 2.5 ? 'Strong' : avg >= 1.5 ? 'Adequate' : 'Needs Development';
      };

      setRecentSessions((recentData || []).map(s => ({
        ...s,
        quality: getQuality(Array.isArray(s.quality_assessments) ? s.quality_assessments[0] : s.quality_assessments)
      })));
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
    loadingRef.current = false;
    setRangeLoading(false);
    setInitialLoad(false);
  };

  const getTrend = (current, previous) => {
    if (previous === null || previous === undefined || previous === 0) return null;
    const pct = Math.round(((current - previous) / previous) * 100);
    return pct;
  };

  const TrendBadge = ({ current, previous }) => {
    const pct = getTrend(current, previous);
    if (pct === null) return null;
    const isUp = pct > 0;
    const isDown = pct < 0;
    return (
      <span className={`text-xs font-medium ${isUp ? 'text-emerald-500' : isDown ? 'text-red-400' : isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
        {isUp ? '+' : ''}{pct}%
      </span>
    );
  };

  const qualityTotal = quality.strong + quality.adequate + quality.needsDev;
  const qualityData = [
    { name: 'Strong', value: quality.strong, color: '#586f8d' },
    { name: 'Adequate', value: quality.adequate, color: '#7662ac' },
    { name: 'Needs Dev', value: quality.needsDev, color: '#e8edf2' },
  ].filter(d => d.value > 0);

  const timeRangeLabel = timeRange === 7 ? 'the past week' : timeRange === 28 ? 'the past month' : timeRange === 90 ? 'the past 3 months' : timeRange === 365 ? 'the past year' : 'all time';
  const formattedMinutes = stats.totalMinutes.toLocaleString();

  const cardClass = `rounded-lg border p-5 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  // Shared header: title + range switcher. Rendered identically during range
  // reloads so the switcher never disappears mid-click.
  const headerEl = (
    <div className="flex items-center justify-between">
      <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
        Dashboard
      </h1>
      <div className={`flex items-center rounded-lg border p-1 gap-0.5 ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
        {timeRangeOptions.map(opt => (
          <button
            key={opt.label}
            onClick={() => setTimeRange(opt.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              timeRange === opt.value
                ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                : isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (initialLoad || rangeLoading) {
    const sk = isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]';
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header: placeholder on first load, live switcher on range reloads */}
        {initialLoad ? (
          <div className="flex items-center justify-between">
            <div className={`h-7 w-32 rounded ${sk}`} />
            <div className={`h-9 w-56 rounded-lg ${sk}`} />
          </div>
        ) : (
          <div className="animate-none">{headerEl}</div>
        )}
        {/* Summary Banner */}
        <div className={`${cardClass} h-20`}>
          <div className={`h-3 w-20 rounded mb-3 ${sk}`} />
          <div className={`h-5 w-80 rounded ${sk}`} />
        </div>
        {/* 3 Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <div key={i} className={cardClass}>
              <div className="flex items-center justify-between mb-3">
                <div className={`h-3 w-24 rounded ${sk}`} />
                <div className={`h-4 w-4 rounded ${sk}`} />
              </div>
              <div className={`h-8 w-20 rounded ${sk}`} />
              <div className={`h-3 w-32 rounded mt-3 ${sk}`} />
            </div>
          ))}
        </div>
        {/* 2 Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1,2].map(i => (
            <div key={i} className={cardClass}>
              <div className={`h-3 w-28 rounded mb-4 ${sk}`} />
              <div className={`h-56 rounded ${sk} opacity-30`} />
            </div>
          ))}
        </div>
        {/* Quality + Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className={cardClass}>
            <div className={`h-3 w-32 rounded mb-4 ${sk}`} />
            <div className={`h-40 w-40 rounded-full mx-auto ${sk} opacity-30`} />
          </div>
          <div className={`${cardClass} lg:col-span-2`}>
            <div className={`h-3 w-28 rounded mb-4 ${sk}`} />
            <div className="space-y-3">
              {[1,2,3,4].map(i => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`h-10 flex-1 rounded ${sk} opacity-30`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {headerEl}

      {/* Summary Banner */}
      <div className={`rounded-lg border p-6 flex items-center justify-between ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
        <div>
          <div className={`text-xs uppercase tracking-wider mb-1.5 ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>
            In {timeRangeLabel}
          </div>
          <div className={`text-lg font-[Manrope] ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>
            TutorTracer has quality reviewed <span className={`font-semibold ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{formattedMinutes} minutes</span> of lessons
          </div>
        </div>
        <button
          onClick={() => navigate('/lessons')}
          className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors whitespace-nowrap ${
            isDark
              ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]'
              : 'border-[#e5e5e5] text-[#666666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'
          }`}
        >
          View recent lessons
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={cardClass}>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>Lessons Reviewed</span>
            <BookOpen className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`} strokeWidth={1.5} />
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            <span className={`text-3xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
              {stats.processedSessions}
            </span>
            {prevStats && <TrendBadge current={stats.processedSessions} previous={prevStats.processedSessions} />}
          </div>
          <div className={`text-xs mt-2 ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>
            of {stats.totalSessions} total sessions
          </div>
        </div>

        <div className={cardClass}>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>Open Issues</span>
            <AlertTriangle className={`h-4 w-4 ${stats.openIssues > 0 ? 'text-amber-500' : isDark ? 'text-[#52525b]' : 'text-[#999999]'}`} strokeWidth={1.5} />
          </div>
          <div className={`text-3xl font-semibold mt-2 font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
            {stats.openIssues}
          </div>
          <button
            onClick={() => navigate('/issues')}
            className={`text-xs mt-2 hover:underline ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}
          >
            View all issues
          </button>
        </div>

        <div className={cardClass}>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>Total Session Time</span>
            <Clock className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`} strokeWidth={1.5} />
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            <span className={`text-3xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
              {Math.floor(stats.totalMinutes / 60)}h {stats.totalMinutes % 60}m
            </span>
            {prevStats && <TrendBadge current={stats.totalMinutes} previous={prevStats.totalMinutes} />}
          </div>
          <div className={`text-xs mt-2 ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>
            across reviewed sessions
          </div>
        </div>
      </div>

      {/* Charts Row — 3 across */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={cardClass}>
          <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>Lesson Volume</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sessionsPerDay}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#27272a' : '#e5e5e5'} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? '#52525b' : '#999999' }}
                  tickFormatter={v => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }}
                  stroke={isDark ? '#27272a' : '#e5e5e5'} />
                <YAxis tick={{ fontSize: 10, fill: isDark ? '#52525b' : '#999999' }} stroke={isDark ? '#27272a' : '#e5e5e5'} width={35} />
                <RechartsTooltip contentStyle={{ backgroundColor: isDark ? '#18181b' : '#fff', border: `1px solid ${isDark ? '#27272a' : '#e5e5e5'}`, borderRadius: '8px', fontSize: '12px' }} />
                <Line type="monotone" dataKey="count" stroke="#586f8d" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={cardClass}>
          <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>Minutes Recorded</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={minutesPerDay}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#27272a' : '#e5e5e5'} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? '#52525b' : '#999999' }}
                  tickFormatter={v => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }}
                  stroke={isDark ? '#27272a' : '#e5e5e5'} />
                <YAxis tick={{ fontSize: 10, fill: isDark ? '#52525b' : '#999999' }} stroke={isDark ? '#27272a' : '#e5e5e5'} width={40} />
                <RechartsTooltip contentStyle={{ backgroundColor: isDark ? '#18181b' : '#fff', border: `1px solid ${isDark ? '#27272a' : '#e5e5e5'}`, borderRadius: '8px', fontSize: '12px' }} />
                <Line type="monotone" dataKey="minutes" stroke="#7662ac" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={cardClass}>
          <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>Quality Distribution</h3>
          {qualityTotal > 0 ? (
            <div className="flex flex-col items-center gap-3">
              <div className="h-32 w-32 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={qualityData} cx="50%" cy="50%" innerRadius={30} outerRadius={50} dataKey="value" strokeWidth={0}>
                      {qualityData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 w-full">
                {qualityData.map(d => (
                  <div key={d.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>{d.name}</span>
                    </div>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                      {Math.round((d.value / qualityTotal) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>No quality data available</div>
          )}
        </div>
      </div>

      {/* Recent Sessions — full width */}
      <div className={cardClass}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>Recent Sessions</h3>
          <button onClick={() => navigate('/lessons')} className={`text-xs hover:underline ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>
            View all
          </button>
        </div>
        <div className="space-y-2">
          {recentSessions.map(s => (
            <div key={s.id} onClick={() => navigate(`/lessons/${s.id}`)}
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${isDark ? 'hover:bg-[#18181b]' : 'hover:bg-[#f5f5f5]'}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.tutor_name}</span>
                  <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>→</span>
                  <span className={`text-sm truncate ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>{s.student_name}</span>
                </div>
                <div className={`text-xs mt-0.5 ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>
                  {s.subject_covered || 'No subject'} · {s.duration_minutes ? `${s.duration_minutes}m` : '—'}
                </div>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  s.quality === 'Strong' ? isDark ? 'bg-[#586f8d]/20 text-[#7662ac]' : 'bg-[#586f8d]/10 text-[#586f8d]'
                  : s.quality === 'Adequate' ? isDark ? 'bg-[#52525b]/30 text-[#a1a1aa]' : 'bg-[#e5e5e5] text-[#666666]'
                  : s.quality === 'Needs Development' ? isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-50 text-amber-700'
                  : isDark ? 'bg-[#27272a] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999999]'
                }`}>{s.quality}</span>
                <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>
                  {new Date(s.lesson_timestamp).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
          {recentSessions.length === 0 && (
            <div className={`text-sm py-8 text-center ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>No sessions found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
